import React, { Fragment, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeHeading = (heading = {}) => ({
  ...heading,
  id: Number(getSalesId(heading) || 0),
  name: String(getSalesValue(heading, 'name', 'Name') || ''),
});

const normalizeCondition = (condition = {}) => ({
  ...condition,
  id: Number(getSalesId(condition) || 0),
  termHeadingId: Number(getSalesValue(condition, 'termHeading', 'TermHeading', 'termHeadingId', 'TermHeadingId') || 0),
  text: String(getSalesValue(condition, 'termText', 'TermText', 'text', 'Text') || ''),
  sortOrder: Number(getSalesValue(condition, 'sortOrder', 'SortOrder', 'id', 'Id') || 0),
  isActive: Boolean(getSalesValue(condition, 'isActive', 'IsActive') ?? true),
});

const normalizeHeadings = (rows) => rows.map(normalizeHeading);
const normalizeConditions = (rows) => rows.map(normalizeCondition);

const TermsConditionsList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    items: headings,
    loading: headingsLoading,
    error: headingsError,
    request,
    endpoints,
    reload: reloadHeadings,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.termsHeadings.base, {
    normalize: normalizeHeadings,
    errorMessage: 'Unable to load term headings.',
  });

  const {
    items: conditions,
    loading: conditionsLoading,
    error: conditionsError,
    reload: reloadConditions,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.termAndConditions.base, {
    normalize: normalizeConditions,
    errorMessage: 'Unable to load terms and conditions.',
  });

  const conditionsByHeadingId = useMemo(() => {
    const grouped = new Map();
    conditions.forEach((condition) => {
      const current = grouped.get(condition.termHeadingId) || [];
      current.push(condition);
      grouped.set(condition.termHeadingId, current);
    });
    grouped.forEach((values) => values.sort((left, right) => left.sortOrder - right.sortOrder));
    return grouped;
  }, [conditions]);

  const filteredTerms = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return headings;
    return headings.filter((term) => term.name.toLowerCase().includes(needle));
  }, [headings, searchTerm]);

  const toggleExpand = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (!canEditSales) return;
    const name = window.prompt('Term heading name');
    if (!name?.trim()) return;
    setSaving(true);
    setActionError('');
    try {
      await request({ method: 'POST', url: endpoints.termsHeadings.base, data: { Name: name.trim() } });
      await reloadHeadings();
    } catch {
      setActionError('Unable to add term heading.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (term) => {
    if (!canEditSales || !term.id) return;
    if (!window.confirm(`Delete ${term.name}?`)) return;
    setSaving(true);
    setActionError('');
    try {
      await request({ method: 'DELETE', url: endpoints.termAndConditions.deleteWithHeading(term.id) });
      await Promise.all([reloadHeadings(), reloadConditions()]);
    } catch {
      setActionError('Unable to delete term heading.');
    } finally {
      setSaving(false);
    }
  };

  const loading = headingsLoading || conditionsLoading;

  return (
    <section className="sales-portal-page" aria-labelledby="sales-terms-title">
      <Row className="mb-3">
        <Col><h4 id="sales-terms-title">Terms and Conditions</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Button className="btn-success addbtnview" onClick={handleAdd} disabled={!canEditSales || saving}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Term Heading
          </Button>
        </Col>
      </Row>

      {(headingsError || conditionsError || actionError) && <Alert variant="danger">{headingsError || conditionsError || actionError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ width: '4%' }}></th>
                  <th style={{ width: '90%' }}>Terms and Conditions</th>
                  <th style={{ width: '6%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading terms...</div></td></tr>
                ) : filteredTerms.length === 0 ? (
                  <tr>
                    <td colSpan={3}><div className="sales-no-data">No terms found.</div></td>
                  </tr>
                ) : (
                  filteredTerms.map((term) => {
                    const isExpanded = expandedIds.has(term.id);
                    const termConditions = conditionsByHeadingId.get(term.id) || [];
                    return (
                      <Fragment key={term.id}>
                        <tr className={`table-border${isExpanded ? ' vr-expanded-row' : ''}`}>
                          <td>
                            <button
                              type="button"
                              className="edit-icon"
                              onClick={() => toggleExpand(term.id)}
                              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${term.name}`}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            </button>
                          </td>
                          <td>{term.name}</td>
                          <td>
                            <button type="button" className="edit-icon" disabled={!canEditSales || saving} onClick={() => handleDelete(term)} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={3} style={{ padding: '0 0 0 30px', background: '#f8f9fa' }}>
                              {termConditions.length === 0 ? (
                                <div className="p-3 text-muted">No conditions defined for this term heading.</div>
                              ) : (
                                <Table size="sm" className="mb-0">
                                  <thead>
                                    <tr className="table-border">
                                      <th style={{ width: '5%' }}>Order</th>
                                      <th>Condition Text</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {termConditions.map((condition) => (
                                      <tr key={condition.id} className="table-border">
                                        <td>{condition.sortOrder}</td>
                                        <td>{condition.text}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsConditionsList;
